import { useState, useEffect } from 'react';
import { updateProfile, changePassword } from '../../../api/users';

/**
 * UserProfile 自定义 Hook
 * 管理用户配置页面的所有状态和逻辑
 */
export function useUserProfile(user, onProfileUpdated, onRefreshUser) {
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState(user || {});
  const [editData, setEditData] = useState({
    realName: '',
    position: '',
    company: '',
    email: '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [isSaving, setIsSaving] = useState(false);

  // 同步用户数据
  useEffect(() => {
    if (user) {
      setProfileData(user);
      setEditData({
        realName: user.realName || '',
        position: user.position || '',
        company: user.company || '',
        email: user.email || '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  }, [user]);

  // 处理表单输入变化
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 重置编辑表单
  const resetEditForm = () => {
    if (!profileData) return;
    
    setEditData({
      realName: profileData.realName || '',
      position: profileData.position || '',
      company: profileData.company || '',
      email: profileData.email || '',
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setFeedback({ type: '', message: '' });
  };

  // 提交表单
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!profileData) return;

    const shouldChangePassword = editData.oldPassword || editData.newPassword || editData.confirmPassword;
    
    // 验证密码
    if (shouldChangePassword && editData.newPassword !== editData.confirmPassword) {
      setFeedback({ type: 'error', message: '新密码与确认密码不一致' });
      return;
    }

    try {
      setIsSaving(true);
      setFeedback({ type: '', message: '' });

      // 更新配置
      const updatedProfile = await updateProfile({
        realName: editData.realName.trim(),
        position: editData.position.trim(),
        company: editData.company.trim(),
        email: editData.email.trim(),
        avatar: profileData.avatar || '',
      });

      setProfileData(updatedProfile);
      if (onProfileUpdated) {
        onProfileUpdated(updatedProfile);
      }

      // 如果需要修改密码
      if (shouldChangePassword) {
        const passwordResult = await changePassword({
          oldPassword: editData.oldPassword.trim(),
          newPassword: editData.newPassword.trim(),
        });
        
        setFeedback({ 
          type: 'success', 
          message: '个人信息和密码已成功更新！' 
        });
      } else {
        setFeedback({ 
          type: 'success', 
          message: '个人信息已成功更新！' 
        });
      }

      // 刷新用户数据
      if (onRefreshUser) {
        onRefreshUser();
      }

      // 清空密码字段
      setEditData((prev) => ({
        ...prev,
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));

      // 3秒后清除提示
      setTimeout(() => {
        setFeedback({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setFeedback({ 
        type: 'error', 
        message: error.message || '更新失败，请重试' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    activeTab,
    setActiveTab,
    profileData,
    editData,
    feedback,
    isSaving,
    handleEditChange,
    resetEditForm,
    handleSubmit,
  };
}

